import usePinnedColumns from "src/hooks/usePinnedColumns";
import CommonTable from "../Tables/CommonTable";
import useHistoryStore from "src/store/historyStore";
import {
  formatCurrency,
  formatEasyDate,
  rgbaObjectToString,
  showNotification,
  transactionDescription,
} from "src/helpers/utils";
import {
  MdOutlineCreditScore,
  MdOutlineDelete,
  MdOutlineToggleOff,
  MdOutlineToggleOn,
  MdOutlineTune,
} from "react-icons/md";
import { useEffect, useMemo, useState } from "react";
import { ICellRendererParams } from "ag-grid-community";
import { ETransactionType, ICON_SIZES } from "src/helpers/constants";
import useCurrentAccount from "src/hooks/useCurrentAccount";
import useColors from "src/hooks/useColors";
import clsx from "clsx";
import { useNavigate } from "react-router-dom";
import RecurringTransactionPayment from "./RecurringTransactionPayment";
import RecurringTransactionSettings from "./RecurringTransactionSettings";
import ConfirmDialog from "../ConfirmDialog/ConfirmDialog";
import { updateTransaction } from "src/api/transactions";
import useCache from "src/hooks/useCache";
import useAllTransactions from "src/hooks/useAllTransactions";
import ClientProviderInfoModal from "../ClientProviderInfoModal/ClientProviderInfoModal";
import { EButtonKind } from "../Button/ButtonTypes";
import { ETransactionsTableKeys } from "../Tables/tableIds";

interface IRecurringTransactionsTable {
  transactions: any[];
  onSelectionChange: (rows: Array<any>) => void;
  singleSelection?: boolean;
}

const RecurringTransactionsTable: React.FC<IRecurringTransactionsTable> = ({
  transactions,
  onSelectionChange = () => {},
  singleSelection = false,
}) => {
  const { currencySymbol } = useCurrentAccount();
  const { isPinnedColumn, updatePinnedColumns } = usePinnedColumns();
  const [showSourceInfo, setShowSourceInfo] = useState<boolean>(false);
  const [sourceToShowDetailsOf, setSourceToShowDetailsOf] =
    useState<string>("");
  const { historyIsEnabled, isTransactionInHistory, markTransactionAsOpened } =
    useHistoryStore();
  const { colors } = useColors();
  const { getCategoryCustomSettings, getSourceCustomSettings } =
    useAllTransactions();
  const navigate = useNavigate();
  const [showRecurringSettings, setShowRecurringSettings] =
    useState<boolean>(false);
  const [showPaymentModal, setShowPaymentModal] = useState<boolean>(false);
  const [selectedTransaction, setSelectedTransaction] = useState<any>(null);
  const [showDeleteDialog, setShowDeleteDialog] = useState<boolean>(false);
  const [isDeletingSettings, setIsDeletingSettings] = useState<boolean>(false);
  const { clearCache } = useCache();

  const deleteRecurringSettings = async () => {
    setIsDeletingSettings(true);
    try {
      const result = await updateTransaction({
        transactionId: selectedTransaction?.invoice,
        fieldsToUpdate: {
          recurring: {
            settings: {
              frequency: "",
              customInterval: "",
              createdAt: "",
              disabledAt: "",
            },
            enabled: false,
            nextPayment: "",
          },
        },
      });

      if (result?.success) {
        await clearCache();

        setIsDeletingSettings(false);
        setShowDeleteDialog(false);

        showNotification({
          message: "Transacción actualizada correctamente",
          kind: "success",
        });
      } else {
        setIsDeletingSettings(false);
        showNotification({
          message: "Ocurrió un error al actualizar la transacción",
          kind: "error",
        });
      }
    } catch (e) {
      setIsDeletingSettings(false);
      showNotification({
        message: "Ocurrió un error al actualizar la transacción",
        kind: "error",
      });
    }
  };

  const columns: any = useMemo(
    () => [
      {
        suppressFloatingFilterButton: false,
        suppressFiltersToolPanel: false,
        field: "recurring.nextPayment",
        floatingFilter: false,
        headerName: "FECHA",
        pinned: isPinnedColumn("recurringTransactionsDatePinned"),
        headerComponentParams: {
          innerHeaderComponent: "TableHeaderTogglePinned",
          innerHeaderComponentParams: {
            onToggle: (isPinned: boolean) => {
              updatePinnedColumns({
                recurringTransactionsDatePinned: isPinned,
              });
            },
          },
        },
        filterParams: {
          comparator: (filterDate: any, cellValue: any) => {
            const cellDate = new Date(cellValue);
            return cellDate.getTime() - filterDate.getTime();
          },
          filterPlaceholder: "Filtrar...",
        },
        filter: "agDateColumnFilter",
        cellRenderer: "CellWithBackgroundRenderer",
        cellRendererParams: (params: any) => {
          return {
            params: {
              ...params,
              value: formatEasyDate({ dateToFormat: params.value }),
            },
            custom: {
              background: "#555",
              color: "#fff",
              className: "text-right",
              onClick: () => {
                const { data } = params;

                if (historyIsEnabled && !isTransactionInHistory(data.invoice)) {
                  markTransactionAsOpened(data.invoice);
                }

                navigate(`/transaction/${data?.account}/${data?.invoice}`);
              },
            },
          };
        },
        // minWidth: 150,
      },
      {
        suppressFloatingFilterButton: false,
        suppressFiltersToolPanel: false,
        field: "payTo",
        floatingFilter: false,
        filter: "agTextColumnFilter",
        filterParams: {
          filterPlaceholder: "Filtrar...",
        },
        headerName: "ORIGEN / DESTINO",
        pinned: isPinnedColumn("recurringTransactionsClientProviderPinned"),
        headerComponentParams: {
          innerHeaderComponent: "TableHeaderTogglePinned",
          innerHeaderComponentParams: {
            onToggle: (isPinned: boolean) => {
              updatePinnedColumns({
                recurringTransactionsClientProviderPinned: isPinned,
              });
            },
          },
        },
        cellRenderer: "CellWithBackgroundRenderer",
        cellRendererParams: (params: ICellRendererParams) => {
          const info = getSourceCustomSettings(params?.value);

          return {
            params,
            custom: {
              background: rgbaObjectToString(info?.bg),
              color: rgbaObjectToString(info?.color),
              onClick: (e: any) => {
                setSourceToShowDetailsOf(params?.value);
                setShowSourceInfo(true);
              },
            },
          };
        },
        // minWidth: 200,
      },
      {
        suppressFloatingFilterButton: false,
        suppressFiltersToolPanel: false,
        field: "description",
        valueFormatter: (params: any) => {
          return transactionDescription(params?.data);
        },
        floatingFilter: false,
        filter: "agTextColumnFilter",
        headerName: "DESCRIPCIÓN",
        // minWidth: 200,
        filterParams: {
          filterPlaceholder: "Filtrar...",
        },
        pinned: isPinnedColumn("recurringTransactionsDescriptionPinned"),
        headerComponentParams: {
          innerHeaderComponent: "TableHeaderTogglePinned",
          innerHeaderComponentParams: {
            onToggle: (isPinned: boolean) => {
              updatePinnedColumns({
                recurringTransactionsDescriptionPinned: isPinned,
              });
            },
          },
        },
        cellRenderer: (params: ICellRendererParams) => {
          return (
            <div
              onClick={() => {
                const { data } = params;

                if (historyIsEnabled && !isTransactionInHistory(data.invoice)) {
                  markTransactionAsOpened(data.invoice);
                }

                navigate(`/transaction/${data?.account}/${data?.invoice}`);
              }}
              className={clsx([
                "hover:underline",
                "hover:cursor-pointer",
                "inline",
              ])}
            >
              {transactionDescription(params?.data)}
            </div>
          );
        },
      },
      {
        suppressFloatingFilterButton: false,
        suppressFiltersToolPanel: false,
        field: "category",
        floatingFilter: false,
        filter: "agTextColumnFilter",
        headerName: "CATEGORÍA",
        // minWidth: 200,
        cellRenderer: "CellWithBackgroundRenderer",
        filterParams: {
          filterPlaceholder: "Filtrar...",
        },
        pinned: isPinnedColumn("recurringTransactionsCategoryPinned"),
        headerComponentParams: {
          innerHeaderComponent: "TableHeaderTogglePinned",
          innerHeaderComponentParams: {
            onToggle: (isPinned: boolean) => {
              updatePinnedColumns({
                recurringTransactionsCategoryPinned: isPinned,
              });
            },
          },
        },
        cellRendererParams: (params: ICellRendererParams) => {
          const cat = getCategoryCustomSettings(params?.value);

          return {
            params: {
              ...params,
              value: params?.value || "Sin categoría",
            },
            custom: {
              background: rgbaObjectToString(cat?.bg),
              color: rgbaObjectToString(cat?.color),
              onClick: () => {
                const { data } = params;

                if (historyIsEnabled && !isTransactionInHistory(data.invoice)) {
                  markTransactionAsOpened(data.invoice);
                }

                navigate(`/transaction/${data?.account}/${data?.invoice}`);
              },
            },
          };
        },
      },
      {
        suppressFloatingFilterButton: false,
        suppressFiltersToolPanel: false,
        field: "total",
        width: 150,
        floatingFilter: false,
        headerName: "TOTAL",
        filterParams: {
          filterPlaceholder: "Filtrar...",
        },
        cellRenderer: "CellWithBackgroundRenderer",
        filter: "agNumberColumnFilter",
        pinned: isPinnedColumn("recurringTransactionsTotalPinned"),
        headerComponentParams: {
          innerHeaderComponent: "TableHeaderTogglePinned",
          innerHeaderComponentParams: {
            onToggle: (isPinned: boolean) => {
              updatePinnedColumns({
                recurringTransactionsTotalPinned: isPinned,
              });
            },
          },
        },
        cellRendererParams: (params: ICellRendererParams) => {
          const isIncome = params?.data?.type === ETransactionType.INCOME;

          return {
            params: {
              ...params,
              value: formatCurrency({
                value: params?.data?.total,
                currency: currencySymbol,
              }),
            },
            custom: {
              background: isIncome ? colors.income.bg : colors.expenses.bg,
              color: isIncome ? colors.income.color : colors.expenses.color,
              onClick: () => {
                const { data } = params;

                if (historyIsEnabled && !isTransactionInHistory(data.invoice)) {
                  markTransactionAsOpened(data.invoice);
                }

                navigate(`/transaction/${data?.account}/${data?.invoice}`);
              },
            },
          };
        },
        // minWidth: 170,
      },
      {
        suppressFloatingFilterButton: false,
        suppressFiltersToolPanel: false,
        floatingFilter: false,
        filter: "agTextColumnFilter",
        headerName: "ESTADO",
        // minWidth: 200,
        filterParams: {
          filterPlaceholder: "Filtrar...",
        },
        pinned: isPinnedColumn("recurringTransactionsDescriptionPinned"),
        headerComponentParams: {
          innerHeaderComponent: "TableHeaderTogglePinned",
          innerHeaderComponentParams: {
            onToggle: (isPinned: boolean) => {
              updatePinnedColumns({
                recurringTransactionsDescriptionPinned: isPinned,
              });
            },
          },
        },
        cellRenderer: (params: ICellRendererParams) => {
          return (
            <div
              onClick={() => {
                const { data } = params;
                setSelectedTransaction(data);
                setShowRecurringSettings(true);
              }}
              className={clsx([
                "hover:underline",
                "hover:cursor-pointer",
                "inline",
              ])}
            >
              {params?.data?.recurring?.enabled
                ? "Habilitada"
                : "Deshabilitada"}
            </div>
          );
        },
      },

      {
        cellRenderer: "CellWithButtonsRenderer",
        headerName: "ACCIONES",
        // minWidth: 80,
        pinned: isPinnedColumn("recurringTransactionsActionsPinned"),
        headerComponentParams: {
          innerHeaderComponent: "TableHeaderTogglePinned",
          innerHeaderComponentParams: {
            onToggle: (isPinned: boolean) => {
              updatePinnedColumns({
                recurringTransactionsActionsPinned: isPinned,
              });
            },
          },
        },
        cellRendererParams: (params: any) => {
          const alreadyOpened =
            historyIsEnabled && isTransactionInHistory(params.data.invoice);
          const attachments = params?.data?.attachments;

          return {
            buttons: [
              {
                icon: params?.data?.recurring?.enabled ? (
                  <MdOutlineTune size={ICON_SIZES.SM} className=" shrink-0" />
                ) : (
                  <MdOutlineTune size={ICON_SIZES.SM} className=" shrink-0" />
                ),
                onClick: () => {
                  const { data } = params;
                  setSelectedTransaction(data);
                  setShowRecurringSettings(true);
                },
                className: clsx(["grid-button"]),
              },
              {
                icon: <MdOutlineDelete size={ICON_SIZES.SM} />,
                onClick: () => {
                  const { data } = params;
                  setSelectedTransaction(data);
                  setShowDeleteDialog(true);
                },
                className: clsx(["grid-button"]),
                hide: params?.data?.recurring?.enabled,
                kind: EButtonKind.DANGER,
              },
              {
                icon: <MdOutlineCreditScore size={ICON_SIZES.SM} />,
                onClick: () => {
                  const { data } = params;
                  setSelectedTransaction(data);
                  setShowPaymentModal(true);
                },
                className: clsx(["grid-button"]),
                hide: !params?.data?.recurring?.enabled,
                kind: EButtonKind.SAVE,
              },
            ],
          };
        },
      },
    ],
    [
      colors?.expenses?.color,
      colors?.income?.color,
      colors?.expenses?.bg,
      colors?.income?.bg,
      currencySymbol,
      historyIsEnabled,
      getCategoryCustomSettings,
      isPinnedColumn,
    ]
  );

  useEffect(() => {
    if (!showSourceInfo) setSourceToShowDetailsOf("");
  }, [showSourceInfo]);

  return (
    <div className="flex w-full relative">
      <CommonTable
        data={transactions}
        columns={columns}
        onSelectionChange={onSelectionChange}
        settingsKey={ETransactionsTableKeys.RECURRING_TRANSACTIONS}
        singleSelection={singleSelection}
      />
      {showRecurringSettings && (
        <RecurringTransactionSettings
          onClose={() => {
            setSelectedTransaction("");
            setShowRecurringSettings(false);
          }}
          transaction={selectedTransaction?.invoice}
          currentSettings={selectedTransaction?.recurring}
          onSave={() => {
            setSelectedTransaction("");
            setShowRecurringSettings(false);
          }}
        />
      )}

      {showPaymentModal && (
        <RecurringTransactionPayment
          transaction={selectedTransaction}
          onClose={() => {
            setSelectedTransaction("");
            setShowPaymentModal(false);
          }}
        />
      )}

      {showDeleteDialog && (
        <ConfirmDialog
          title="Eliminar"
          description="¿Deseas eliminar la recurrencia de esta transacción?"
          note="La transacción no se eliminará, sólamente su configuración."
          onClose={() => {
            if (!isDeletingSettings) {
              setShowDeleteDialog(false);
            }
          }}
          onConfirm={async () => {
            await deleteRecurringSettings();
          }}
          confirmKind={EButtonKind.DANGER}
          isLoading={isDeletingSettings}
        />
      )}

      {!!(showSourceInfo && !!sourceToShowDetailsOf) && (
        <ClientProviderInfoModal
          onClose={() => setShowSourceInfo(false)}
          clientProvider={sourceToShowDetailsOf}
        />
      )}
    </div>
  );
};

export default RecurringTransactionsTable;
